import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Menu, Home, Users, FileText, Receipt, Clock, BarChart3, LogOut, User, Briefcase, FileSpreadsheet, Settings, Percent, ArrowDownUp, DollarSign, Wallet, Activity, BookOpen, MessageCircle, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard', section: 'Overview' },
    { icon: Users, label: 'Clients', path: '/clients', section: 'Operations' },
    { icon: Briefcase, label: 'Consultants', path: '/consultants', section: 'Operations' },
    { icon: FileText, label: 'Projects & POs', path: '/projects', section: 'Operations' },
    { icon: Clock, label: 'Time Entries', path: '/time-entries', section: 'Operations' },
    { icon: MessageCircle, label: 'Communications', path: '/communications', section: 'Operations' },
    { icon: FileSpreadsheet, label: 'Invoices', path: '/invoices', section: 'Accounting' },
    { icon: Receipt, label: 'Receipts', path: '/receipts', section: 'Accounting' },
    { icon: BookOpen, label: 'Ledger', path: '/ledger', section: 'Accounting' },
    { icon: Landmark, label: 'Bank Reconciliation', path: '/bank-reconciliation', section: 'Accounting' },
    { icon: DollarSign, label: 'Expenses', path: '/expenses', section: 'Accounting' },
    { icon: Wallet, label: 'Consultant Payments', path: '/consultant-payments', section: 'Accounting' },
    { icon: Percent, label: 'HR Commissions', path: '/hr-commissions', section: 'Accounting' },
    { icon: BarChart3, label: 'Reports', path: '/reports', section: 'Accounting' },
  ];

  const menuSections = menuItems.reduce((sections, item) => {
    const sectionTitle = item.section ?? 'General';
    let section = sections.find((entry) => entry.title === sectionTitle);

    if (!section) {
      section = { title: sectionTitle, items: [] };
      sections.push(section);
    }

    section.items.push(item);
    return sections;
  }, []);

  const adminMenuItems = [
    { icon: ArrowDownUp, label: 'Import & Export', path: '/import-export' },
    { icon: Settings, label: 'User Management', path: '/users' },
    { icon: Activity, label: 'Activity Logs', path: '/activity-logs' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="sidebar-toggle">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/utis-logo.png" 
                alt="UTIS Logo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-utis-forest">UTIS Finance Suite</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">{user?.full_name}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="pt-20 flex">
        {/* Sidebar */}
        {sidebarOpen && (
          <aside className="w-64 bg-white border-r border-gray-200 fixed left-0 top-20 bottom-0 overflow-y-auto">
            <nav className="p-4 space-y-2">
              {menuSections.map((section, index) => (
                <div key={section.title} className="space-y-1">
                  <div className={`px-4 ${index === 0 ? 'pt-2' : 'pt-4'} pb-2`}>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {section.title}
                    </div>
                  </div>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-utis-sage/20 text-utis-forest font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ))}

              {/* Admin Section */}
              {user?.role === 'Admin' && (
                <>
                  <div className="pt-4 pb-2 px-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Administration
                    </div>
                  </div>
                  {adminMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-utis-sage/20 text-utis-forest font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </nav>
          </aside>
        )}

        {/* Main Content */}
        <main className={`flex-1 p-8 ${sidebarOpen ? 'ml-64' : ''}`}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
